import React from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  CheckCircle,
  Star,
  Users,
  Bot
} from 'lucide-react';

const SEOContent = () => {
  const localSEOFaqs = [
    {
      question: "What AI automation services does Local Finder LLC provide in Pasadena?",
      answer: "Local Finder LLC provides comprehensive AI automation services including custom chatbots for 24/7 customer support, intelligent email automation sequences, social media automation, and proprietary software development. We specialize in creating custom solutions like FTC compliance software, lead management systems, quote generators, and Reddit marketing automation tools. Our AI solutions are designed specifically for local businesses in Pasadena and throughout California."
    },
    {
      question: "How can Local Finder LLC help my Pasadena business rank higher in Google search results?",
      answer: "Our local SEO expertise helps Pasadena businesses dominate Google search results through comprehensive optimization strategies. We optimize your Google My Business profile, implement local keyword targeting, create location-specific content, manage online reviews, and use our proprietary SEO AI analysis tools. Many of our clients achieve front-page Google rankings within 6 months, significantly increasing their local visibility and customer acquisition."
    },
    {
      question: "Does Local Finder LLC develop custom software for small businesses?",
      answer: "Yes, Local Finder LLC specializes in developing proprietary software solutions tailored to each business's unique needs. Our custom software development includes FTC compliance automation tools, advanced lead management systems, automated quote generators, option flows readers, and specialized marketing automation platforms. We also develop internal AI tools for SEO optimization and GEO targeting that give our clients competitive advantages in their markets."
    },
    {
      question: "What makes Local Finder LLC different from other marketing agencies in Pasadena?",
      answer: "Local Finder LLC combines over 10 years of proven digital marketing expertise with cutting-edge AI automation and custom software development capabilities. Unlike traditional marketing agencies, we develop proprietary technology solutions including compliance software, internal SEO tools, and specialized automation platforms. Our unique approach of 'GetBetterBiggerFasterVisibility' delivers measurable results through both traditional marketing strategies and innovative AI-powered solutions."
    },
    {
      question: "How long does it take to see results from Local Finder LLC's marketing services?",
      answer: "Results vary depending on the service, but many of our Pasadena clients see improvements within the first month. Our AI chatbots start converting visitors immediately, email automation campaigns typically show results within 2-4 weeks, and our local SEO strategies often achieve first-page Google rankings within 6 months. We've helped businesses increase their online visibility, generate more leads, and achieve significant growth in customer acquisition and revenue."
    },
    {
      question: "Does Local Finder LLC serve businesses outside of Pasadena, California?",
      answer: "While Local Finder LLC is proudly based in Pasadena, CA at 12 S Raymond Ave, we serve local businesses throughout the United States. Our AI automation, custom software development, and digital marketing services can be implemented remotely, allowing us to help businesses nationwide achieve their growth goals. We maintain our local expertise while serving clients across various industries and geographic locations."
    }
  ];

  const serviceAreas = [
    'Pasadena, CA', 'Los Angeles, CA', 'Glendale, CA', 'Burbank, CA',
    'Altadena, CA', 'San Marino, CA', 'Arcadia, CA', 'Monrovia, CA'
  ];

  const industries = [
    'Higher Education & Universities', 'Restaurants & Food Service', 'Healthcare & Dental Practices', 
    'Legal Services & Law Firms', 'Real Estate Agencies',
    'Retail & E-commerce', 'Professional Services', 
    'Home Services & Contractors', 'Automotive Services'
  ];

  return (
    <div className="bg-neutral-50">
      {/* FAQ Section for AI Search Optimization */}
      <section className="section-padding" id="faq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center space-x-2 bg-primary-100 rounded-full px-4 py-2 mb-6"
            >
              <HelpCircle className="text-primary-600" size={16} />
              <span className="text-primary-700 text-sm font-medium">Frequently Asked Questions</span>
            </motion.div>
            
            <h2 className="text-4xl lg:text-5xl font-black text-neutral-900 mb-6">
              Common Questions About
              <span className="text-gradient block">Our AI Marketing Services</span>
            </h2>
            
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Get answers to the most common questions about Local Finder LLC's AI automation, 
              custom software development, and local SEO services in Pasadena, California.
            </p>
          </motion.div>

          {/* FAQ Items */}
          <div className="max-w-4xl mx-auto space-y-6">
            {localSEOFaqs.map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden group"
              >
                <summary className="flex items-center justify-between p-8 cursor-pointer hover:bg-neutral-50 transition-colors duration-300">
                  <h3 className="text-lg font-semibold text-neutral-900 pr-4">{faq.question}</h3>
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-primary-600 group-open:rotate-45 transition-transform duration-300" size={18} />
                  </div>
                </summary>
                <div className="px-8 pb-8">
                  <p className="text-neutral-700 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <div className="flex items-center justify-center space-x-2 mb-6">
              <MapPin className="text-white" size={24} />
              <h2 className="text-3xl font-bold">Global Reach, Local Expertise</h2>
            </div>
            <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
              Whether you're a local business in Pasadena, a regional company in California, 
              or an international brand seeking global visibility, Local Finder LLC provides 
              AI-powered automation, SEO, and digital marketing strategies tailored to your growth.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center"
              >
                <div className="text-2xl font-bold mb-2">🏢</div>
                <span className="font-semibold">Pasadena, CA</span>
                <div className="text-sm opacity-80 mt-1">Headquarters</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center"
              >
                <div className="text-2xl font-bold mb-2">🌴</div>
                <span className="font-semibold">All of California</span>
                <div className="text-sm opacity-80 mt-1">Statewide Services</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center"
              >
                <div className="text-2xl font-bold mb-2">🇺🇸</div>
                <span className="font-semibold">United States</span>
                <div className="text-sm opacity-80 mt-1">Nationwide Coverage</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center"
              >
                <div className="text-2xl font-bold mb-2">🌍</div>
                <span className="font-semibold">Worldwide</span>
                <div className="text-sm opacity-80 mt-1">International Brands</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center space-x-2 mb-6">
              <Users className="text-primary-600" size={24} />
              <h2 className="text-3xl font-bold text-neutral-900">Industries We Transform</h2>
            </div>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto mb-8">
              Local Finder LLC has successfully implemented AI automation and marketing solutions 
              across diverse industries, helping local businesses achieve remarkable growth.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-neutral-200 rounded-lg p-4 text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <span className="text-neutral-700 font-medium">{industry}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Local Business Contact Information */}
      <section className="py-16 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Our Location</h3>
              <p className="text-neutral-300">
                12 S Raymond Ave<br />
                Pasadena, CA 91105<br />
                United States
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Phone Number</h3>
              <p className="text-neutral-300">
                <a href="tel:+12133754006" className="hover:text-primary-300 transition-colors duration-300">
                  (213) 375-4006
                </a>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Email Address</h3>
              <p className="text-neutral-300">
                <a href="mailto:contact@localfinder.biz" className="hover:text-primary-300 transition-colors duration-300">
                  contact@localfinder.biz
                </a>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Business Hours</h3>
              <p className="text-neutral-300">
                Mon-Fri: 9:00 AM - 6:00 PM<br />
                Saturday: 10:00 AM - 4:00 PM<br />
                Sunday: Closed
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOContent;